// Note Review: the "Combine first" banner with actions (10-07, appointment
// 890480 - an 18 s add-on became a second unsigned note and Sign & Post stayed
// disabled with no way out).
//
// BlinkScribe:
//   POST /v1/recording/combine-notes {keep_id, add_ids[, edited_ack, cross_user_ack]} -> 202 {merged_id, poll}
//     409 CROSS_USER_NOTES / EDITED_ADD_NOTE: the reviewer confirms ("Combine anyway");
//     409 ALREADY_COMBINING: someone else started it - this screen follows that merge.
//   GET  /v1/recording/combine-notes/{merged_id} -> {state: queued|processing|done|failed|conflict}
//   POST /v1/recording/{id}/discard {reason?}
// The screen owns the running job ({mergedId, ids}) so Sign & Post and Edit stay
// disabled while it runs and the progress resumes when the panel comes back
// (or after a reload, from daily-review's combine_pending).
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import blinkscribeapi from "../api/blinkscribeapi";
import {
  canDiscard,
  combineErrorText,
  combineIntroText,
  defaultKeepId,
  discardConfirmText,
  keepLineText,
  nextKeepId,
  noteRowText,
} from "./noteVisit";

export const POLL_MS = 3000;
export const POLL_LIMIT_MS = 6 * 60 * 1000;

// epoch: bumped by the screen on every list reload, so a merge whose polling
// stopped (6 minutes) is picked up again after a Refresh.
// Refusals that ask the reviewer to confirm, and the flag that confirms them.
const ACK_FOR_CODE = { CROSS_USER_NOTES: "cross_user_ack", EDITED_ADD_NOTE: "edited_ack" };
// Refusals meaning the list on screen is out of date (reload it).
const STALE_CODES = new Set(["NOT_A_NOTE", "NOT_FOUND", "NOT_FINISHED", "SIGNED", "POSTED", "DIFFERENT_VISIT"]);

const CombineNotesPanel = ({ group, job, epoch = 0, discardEnabled = true, onJobStart, onJobEnd, onDiscarded, onStale, showToast }) => {
  const notes = useMemo(() => (Array.isArray(group) ? group.filter(Boolean) : []), [group]);
  const groupKey = notes.map((n) => n.id).join(",");
  const [keepId, setKeepId] = useState(() => defaultKeepId(notes));
  const [starting, setStarting] = useState(false);
  const [discardNote, setDiscardNote] = useState(null);
  const [discarding, setDiscarding] = useState(false);
  const [ackPrompt, setAckPrompt] = useState(null); // { acks, flag, message }
  const busy = !!job || starting;

  // A different visit / set of notes: back to the default note to keep.
  useEffect(() => {
    setKeepId(defaultKeepId(notes));
    setAckPrompt(null);
    // groupKey captures the membership; notes is derived from it
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupKey]);

  // Poll the running merge until it is done, failed, or 6 minutes passed.
  const endRef = useRef(onJobEnd);
  endRef.current = onJobEnd;
  const mergedId = job?.mergedId;
  useEffect(() => {
    if (!mergedId) return undefined;
    let stopped = false;
    let timer = null;
    const deadline = Date.now() + POLL_LIMIT_MS;
    const poll = async () => {
      if (stopped) return;
      let res = null;
      try {
        res = await blinkscribeapi.get(`/v1/recording/combine-notes/${mergedId}`);
      } catch (err) {
        if (err?.response?.status === 404) {
          if (!stopped) endRef.current?.({ state: "failed", mergedId, message: combineErrorText(err, "") });
          return;
        }
        res = null; // network blip: keep polling until the deadline
      }
      if (stopped) return;
      const state = res?.state;
      if (state === "done" || state === "failed" || state === "conflict") {
        endRef.current?.({ state, mergedId: res?.merged_id || mergedId, message: res?.message || "" });
        return;
      }
      if (Date.now() >= deadline) {
        endRef.current?.({ state: "timeout", mergedId });
        return;
      }
      timer = setTimeout(poll, POLL_MS);
    };
    timer = setTimeout(poll, POLL_MS);
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
    };
  }, [mergedId, epoch]);

  const keep = notes.find((n) => n.id === keepId) || notes[0];
  const addIds = notes.filter((n) => keep && n.id !== keep.id).map((n) => n.id);

  // acks: confirmations already given ({ cross_user_ack, edited_ack }).
  const combine = async (acks = {}) => {
    if (busy || !keep || !addIds.length) return;
    setStarting(true);
    setAckPrompt(null);
    try {
      const body = { keep_id: keep.id, add_ids: addIds, ...acks };
      const res = await blinkscribeapi.post("/v1/recording/combine-notes", body);
      const id = Number(res?.merged_id);
      if (!id) {
        showToast?.(combineErrorText(res, "Could not combine the notes."), "error");
        return;
      }
      if (res?.state === "done") {
        onJobEnd?.({ state: "done", mergedId: id });
        return;
      }
      onJobStart?.({ mergedId: id, ids: notes.map((n) => n.id) });
    } catch (err) {
      const data = err?.response?.data || {};
      const code = data.code;
      const flag = ACK_FOR_CODE[code];
      if (flag && !acks[flag]) {
        setAckPrompt({ acks, flag, message: combineErrorText(err, "Please confirm before combining.") });
        return;
      }
      const runningId = Number(data.merged_id);
      if (code === "ALREADY_COMBINING" && runningId > 0) {
        // another reviewer started combining these notes: follow that merge
        onJobStart?.({ mergedId: runningId, ids: notes.map((n) => n.id) });
        return;
      }
      showToast?.(combineErrorText(err, "Could not combine the notes."), "error");
      if (STALE_CODES.has(code)) onStale?.();
    } finally {
      setStarting(false);
    }
  };

  const confirmDiscard = async () => {
    const n = discardNote;
    if (!n || discarding) return;
    setDiscarding(true);
    try {
      await blinkscribeapi.post(`/v1/recording/${n.id}/discard`, {});
      setDiscardNote(null);
      showToast?.(`Note #${n.id} discarded.`, "success");
      onDiscarded?.(n.id);
    } catch (err) {
      setDiscardNote(null);
      showToast?.(combineErrorText(err, "Could not discard the note."), "error");
      if (STALE_CODES.has(err?.response?.data?.code) || err?.response?.data?.code === "ALREADY_COMBINING") onStale?.();
    } finally {
      setDiscarding(false);
    }
  };

  if (notes.length < 2) return null;

  return (
    <div className="px-5 py-3 border-b bg-[#FFF9EE]" data-testid="combine-notes-panel">
      <p className="text-sm text-gray-800">{combineIntroText(notes)}</p>
      <ul className="mt-2 flex flex-col gap-1">
        {notes.map((n) => (
          <li key={n.id} className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
            <span>{noteRowText(n)}</span>
            {discardEnabled && canDiscard(notes, n) && (
              <button
                type="button"
                onClick={() => setDiscardNote(n)}
                disabled={busy}
                className="text-xs text-gray-500 underline hover:text-gray-700 disabled:opacity-40 disabled:no-underline"
              >
                Discard this note
              </button>
            )}
          </li>
        ))}
      </ul>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => combine({})}
          disabled={busy}
          className={`px-4 py-2 rounded-lg text-sm font-semibold text-white ${busy ? "bg-gray-400 cursor-not-allowed" : "bg-[#1D2951]"}`}
        >
          {busy ? "Combining…" : "Combine into one note"}
        </button>
        {job ? (
          <span className="text-xs text-gray-500" role="status">
            Combining… about a minute. Sign and Edit are paused until it is done.
          </span>
        ) : keep ? (
          <span className="text-xs text-gray-500">
            {keepLineText(notes, keep.id)}{" "}
            <button
              type="button"
              onClick={() => setKeepId(nextKeepId(notes, keep.id))}
              disabled={busy}
              className="underline hover:text-gray-700 disabled:opacity-40"
              aria-label="Change which note is kept"
            >
              change
            </button>
          </span>
        ) : null}
      </div>
      {ackPrompt && (
        <div className="mt-2 text-xs text-gray-700" role="alert">
          <span>{ackPrompt.message} </span>
          <button type="button" onClick={() => combine({ ...ackPrompt.acks, [ackPrompt.flag]: true })}
            disabled={busy} className="underline font-semibold disabled:opacity-40">
            Combine anyway
          </button>{" "}
          <button type="button" onClick={() => setAckPrompt(null)} className="underline">
            Cancel
          </button>
        </div>
      )}

      <Dialog open={!!discardNote} onClose={() => { if (!discarding) setDiscardNote(null); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: "#1D2951" }}>Discard this note?</DialogTitle>
        <DialogContent>
          {discardNote ? <p className="text-sm text-gray-700 leading-relaxed">{discardConfirmText(discardNote)}</p> : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiscardNote(null)} disabled={discarding} autoFocus>Cancel</Button>
          <Button onClick={confirmDiscard} disabled={discarding} color="error">
            {discarding ? "Discarding…" : "Discard note"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CombineNotesPanel;
